/**
 * 값 동기화 plan 응답을 화면이 쓰는 조회표로 바꾼다. React·DOM 무관 순수 로직.
 *
 * 값 반영이 "어느 셀에 어떤 값을 넣을지" 판단하는 유일한 근거다. 화면의 표
 * 모양은 보지 않으므로 좌우 구조가 달라도 태깅이 맞는 셀은 채워지고, 모양이
 * 같아도 태깅이 다르면 채우지 않는다.
 *
 * entries에는 "반영될 항목"만 들어온다. 변환하지 못한 셀은 failures로 빠지므로,
 * 여기서 failures를 따로 걸러낼 필요가 없다 — entries에 있다는 것 자체가
 * failure가 아니라는 뜻이다.
 *
 * 다만 entries에 있다고 다 같은 확신은 아니다. 서버가 위치로 추정해 확정한
 * 셀은 interpolations에 함께 실려 오고, 그 identities로 어느 셀인지 짚을 수
 * 있다. 이걸 화면에서 빼면 사용자는 전부 태그로 정확히 맞춘 것으로 읽는다.
 */

import type {
  IxdValueSyncIdentityFields,
  IxdValueSyncPlanResult,
  IxdValueSyncPlanStatus,
  IxdValueSyncPlanTable,
} from "@/apis/ixd-value-sync/types";
import {
  makeCellIdentityKey,
  type CellIdentityKey,
} from "@/utils/ixd-cell-model";

/** identity key → 반영할 저장값 */
export type PlanValueIndex = ReadonlyMap<CellIdentityKey, string>;

/**
 * 서버가 그 값을 확정한 근거. 태그 키가 정확히 맞은 셀은 여기 담기지 않는다.
 *
 * - `key`   — 라벨 기반 정규 키로 이었다. 확인 대상이지만 근거가 태그에 있다.
 * - `ordinal` — 앵커 사이에 하나만 남아 확정했다. 사람이 봐야 한다.
 */
export type PlanConfidence = "key" | "ordinal";

/** identity key → 추정 근거. 태그로 확정된 셀은 키가 없다. */
export type PlanConfidenceIndex = ReadonlyMap<CellIdentityKey, PlanConfidence>;

/**
 * 한 값이 어느 워크북 표에서 왔는지.
 *
 * 행이 보여주는 단위가 워크북 표라, "지금 보고 있는 표의 값이냐"는 판정도
 * 워크북 표 id로 한다. roleKey는 서로 다른 표가 같은 role을 가질 수 있어
 * 남의 표 값을 이 표 값으로 잘못 말한다.
 */
export interface PlanValueCandidate {
  value: string;
  workbookTableId: string;
}

/**
 * 셀 위에서 확인해야 하는 항목.
 *
 * failures는 여기 들어오지 않는다 — 서버가 대응 IXD 셀을 찾지 못해 실패한 것이라
 * 붙일 셀 자체가 없다. 그건 표 단위 목록이 맡는다.
 */
export type PlanCellNotice =
  | { kind: "not-applied" }
  /** 셀의 통화 표시만 보고 표 단위 배율을 곱했다 — 셀이 배율 없는 금액이면 틀린 값이다 */
  | { kind: "unit-assumed"; currencyMarker: string; scalePower: number };

export type PlanCellNoticeKind = PlanCellNotice["kind"];

export interface PlanIndex {
  status: IxdValueSyncPlanStatus;
  /** 최상위 합계 — 확인 화면 요약에 쓴다 */
  totals: {
    planned: number;
    failures: number;
    interpolations: number;
  };
  /**
   * 워크북 표 id → 그 표가 계획한 (identity → 값).
   *
   * 값 반영은 **보고 있는 표의 값**을 넣는다. 리포트 전체에서 하나의 값을
   * 정하려 들면, 다른 표가 같은 항목을 다르게 태깅했다는 이유로 화면 왼쪽에
   * 뻔히 값이 적혀 있는 표에서조차 아무것도 못 넣게 된다.
   *
   * 한 표 안에서 값이 갈리는 경우만 진짜 모호하다 — 그건 이 맵에서 빠지고
   * conflictByIdentityKey로 간다.
   */
  valuesByWorkbookTableId: ReadonlyMap<
    string,
    ReadonlyMap<CellIdentityKey, string>
  >;
  /** 위치·라벨로 추정해 채운 셀. 화면에 반드시 드러낸다 */
  confidenceByIdentityKey: PlanConfidenceIndex;
  /** 셀 위에 표시할 알림 중 행과 무관한 것 (표 내부 충돌·자리 겹침) */
  cellNoticeByIdentityKey: ReadonlyMap<CellIdentityKey, PlanCellNotice>;
  /**
   * 워크북 표 id → 표 단위 계획들. 행별 진단 표시에 쓴다.
   *
   * 그룹 키가 (워크북 표, role, IXD 표) 조합이라 한 role의 셀이 서로 다른
   * IXD 표로 갈리면 그룹이 둘로 나뉜다. 배열로 받아야 뒤 그룹이 앞 그룹을
   * 덮어쓰지 않는다.
   */
  tablesByWorkbookTableId: ReadonlyMap<string, IxdValueSyncPlanTable[]>;
}

/**
 * plan 응답의 9필드를 셀 identity 키로 바꾼다.
 *
 * 이름이 하나 다르다 — plan은 languageType이 아니라 language로 내려준다.
 * 변환은 이 함수 한 곳에서만 한다.
 *
 * 없는 축·멤버를 null이 아니라 빈 문자열로 주는 것은 makeCellIdentityKey의
 * 정규화와 결과가 같다.
 */
export const planIdentityKey = (
  fields: IxdValueSyncIdentityFields,
): CellIdentityKey =>
  makeCellIdentityKey({
    elementName: fields.elementName,
    elementNamespace: fields.elementNamespace,
    domainName: fields.domainName,
    domainNamespace: fields.domainNamespace,
    dimensionName: fields.dimensionName,
    dimensionNamespace: fields.dimensionNamespace,
    csType: fields.csType,
    languageType: fields.language,
    contextKey: fields.contextKey,
  });

/** 한 셀 안에 근거가 섞여 있으면 낮은 쪽을 택한다 — 안내는 가장 약한 근거를 기준으로 해야 한다. */
const weakestBasis = (table: {
  elements: { basis: PlanConfidence }[];
}): PlanConfidence =>
  table.elements.some(({ basis }) => basis === "ordinal") ? "ordinal" : "key";

const EMPTY_INDEX: PlanIndex = {
  status: "success",
  totals: { planned: 0, failures: 0, interpolations: 0 },
  valuesByWorkbookTableId: new Map(),
  confidenceByIdentityKey: new Map(),
  cellNoticeByIdentityKey: new Map(),
  tablesByWorkbookTableId: new Map(),
};

/**
 * 표별로 값을 잡는다.
 *
 * 표를 가로지르는 값 차이는 막지 않는다 — 사용자가 보고 있는 표에는 값이
 * 하나뿐이고, 버튼의 뜻도 "이 표의 값을 반영"이다. 다른 표의 값을 쓰고 싶으면
 * 그 표로 가서 반영하면 된다. 그게 이 화면의 원래 조작 방식이다.
 */
export const buildPlanIndex = (
  plan: IxdValueSyncPlanResult | undefined,
): PlanIndex => {
  if (!plan) return EMPTY_INDEX;

  const valuesByWorkbookTableId = new Map<
    string,
    Map<CellIdentityKey, string>
  >();
  const sourcesByIdentityKey = new Map<CellIdentityKey, PlanValueCandidate[]>();
  const confidenceByIdentityKey = new Map<CellIdentityKey, PlanConfidence>();
  const cellNoticeByIdentityKey = new Map<CellIdentityKey, PlanCellNotice>();
  const tablesByWorkbookTableId = new Map<string, IxdValueSyncPlanTable[]>();
  const elementNameByIdentityKey = new Map<CellIdentityKey, string>();

  for (const table of plan.tables) {
    const group = tablesByWorkbookTableId.get(table.workbookTableId);
    if (group) group.push(table);
    else tablesByWorkbookTableId.set(table.workbookTableId, [table]);

    if (!valuesByWorkbookTableId.has(table.workbookTableId)) {
      valuesByWorkbookTableId.set(
        table.workbookTableId,
        new Map<CellIdentityKey, string>(),
      );
    }

    for (const entry of table.entries) {
      const identityKey = planIdentityKey(entry);
      elementNameByIdentityKey.set(identityKey, entry.elementName);

      const sources = sourcesByIdentityKey.get(identityKey);
      const candidate = {
        value: entry.value,
        workbookTableId: table.workbookTableId,
      };
      if (!sources) {
        sourcesByIdentityKey.set(identityKey, [candidate]);
      } else if (
        !sources.some(
          (source) =>
            source.workbookTableId === candidate.workbookTableId &&
            source.value === candidate.value,
        )
      ) {
        sources.push(candidate);
      }
    }

    for (const interpolation of table.interpolations) {
      const basis = weakestBasis(interpolation);
      for (const identity of interpolation.identities) {
        const identityKey = planIdentityKey(identity);
        if (!identity.applied) {
          cellNoticeByIdentityKey.set(identityKey, { kind: "not-applied" });
          continue;
        }
        confidenceByIdentityKey.set(identityKey, basis);
      }
    }

    for (const assumption of table.unitAssumptions ?? []) {
      for (const identity of assumption.identities) {
        const identityKey = planIdentityKey(identity);
        // 반영되지 않는 셀은 값이 안 들어가므로 배율 가정보다 미반영이 먼저다.
        if (!identity.applied) {
          cellNoticeByIdentityKey.set(identityKey, { kind: "not-applied" });
          continue;
        }
        if (cellNoticeByIdentityKey.has(identityKey)) continue;
        cellNoticeByIdentityKey.set(identityKey, {
          kind: "unit-assumed",
          currencyMarker: assumption.currencyMarker,
          scalePower: assumption.scalePower,
        });
      }
    }
  }

  /*
   * 값이 갈려도 채운다.
   *
   * 표 단위로 나눠 담으므로 표를 가로질러 갈리는 값은 애초에 서로를 건드리지
   * 않는다 — 보고 있는 표의 값이 그 표에 들어간다.
   *
   * 한 표 안에서 갈리는 경우만 정할 근거가 없다. 계획에 먼저 나온 값을 쓴다 —
   * 무엇을 고르든 임의이므로, 같은 계획이면 같은 결과가 나오는 쪽을 택한다.
   */
  for (const [identityKey, sources] of sourcesByIdentityKey) {
    for (const { workbookTableId, value } of sources) {
      const values = valuesByWorkbookTableId.get(workbookTableId);
      if (values && !values.has(identityKey)) values.set(identityKey, value);
    }
  }

  return {
    status: plan.status,
    totals: {
      planned: plan.plannedCount,
      failures: plan.failureCount,
      interpolations: plan.interpolationCount,
    },
    valuesByWorkbookTableId,
    confidenceByIdentityKey,
    cellNoticeByIdentityKey,
    tablesByWorkbookTableId,
  };
};

/**
 * plan 조회 상태와 결과를 한 덩어리로 묶는다.
 *
 * "아직 못 받음"과 "받았는데 비어 있음"은 화면에서 다르게 말해야 하는데,
 * 조회표만 넘기면 둘 다 빈 Map이라 구분할 수 없다. 상태와 값을 따로 두면
 * 한쪽만 갱신되는 순간 조용히 어긋나므로 union으로 계약을 강제한다.
 */
export type PlanAvailability =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; index: PlanIndex };
